import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import { apiJson } from '../utils/api';

interface FeedbackItem {
  feedback_id: string;
  user_id: string;
  email?: string;
  category?: string;
  message: string;
  app_version?: string;
  created_at?: string;
}

const CATEGORY_COLOR: Record<string, 'red' | 'blue' | 'green' | 'grey'> = {
  bug: 'red',
  idea: 'blue',
  praise: 'green',
  other: 'grey',
};

const AppFeedbackPage: React.FC = () => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson<{ items: FeedbackItem[] }>('/admin/feedback');
      setItems(data.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Free-text feedback submitted from the app.">
          App Feedback
        </Header>
      }
    >
      {error && <Alert type="error">{error}</Alert>}
      <Table
        loading={loading}
        loadingText="Loading feedback"
        items={items}
        variant="full-page"
        wrapLines
        columnDefinitions={[
          {
            id: 'when',
            header: 'When',
            cell: (i) => i.created_at?.slice(0, 16).replace('T', ' ') ?? '—',
            width: 160,
          },
          {
            id: 'category',
            header: 'Type',
            cell: (i) => (
              <Badge color={CATEGORY_COLOR[i.category ?? 'other'] ?? 'grey'}>
                {i.category ?? 'other'}
              </Badge>
            ),
            width: 110,
          },
          { id: 'message', header: 'Message', cell: (i) => i.message, isRowHeader: true },
          {
            id: 'from',
            header: 'From',
            cell: (i) => i.email || i.user_id.slice(0, 8),
            width: 200,
          },
          { id: 'version', header: 'App', cell: (i) => i.app_version ?? '—', width: 90 },
        ]}
        empty={
          <Box textAlign="center" color="text-body-secondary">
            No feedback yet.
          </Box>
        }
        header={<Header counter={`(${items.length})`}>Submissions</Header>}
      />
    </ContentLayout>
  );
};

export default AppFeedbackPage;
